import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { calendarService } from '../services/calendarService';
import { bookingService } from '../services/bookingService';
import { pollService } from '../services/pollService';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUsers, FiBarChart3, FiPlus, FiExternalLink, FiEdit, FiTrash2, FiCopy } = FiIcons;

const DashboardPage = () => {
  const { user } = useAuth();
  const { calendars, polls, refreshCalendars, refreshPolls, deleteCalendar, deletePoll } = useApp();
  const [stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    totalVotes: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        await Promise.all([
          refreshCalendars(),
          refreshPolls()
        ]);

        // Calculate stats
        const bookings = await bookingService.getUserBookings();
        const totalBookings = bookings.length;
        const pendingBookings = bookings.filter(b => b.status === 'pending').length;

        let totalVotes = 0;
        const allPolls = await pollService.getUserPolls();
        allPolls.forEach(poll => {
          poll.options.forEach(option => {
            totalVotes += option.votes.length;
          });
        });

        setStats({
          totalBookings,
          pendingBookings,
          totalVotes
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [refreshCalendars, refreshPolls]);

  const copyShareLink = async (shareId, type = 'calendar') => {
    const baseUrl = window.location.origin;
    const shareUrl = type === 'calendar' 
      ? `${baseUrl}/#/book/${shareId}` 
      : `${baseUrl}/#/poll/${shareId}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      // You could add a toast notification here
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleDeleteCalendar = async (id) => {
    if (window.confirm('Möchten Sie diesen Kalender wirklich löschen?')) {
      try {
        await calendarService.deleteCalendar(id);
        await deleteCalendar(id);
        await refreshCalendars();
      } catch (error) {
        console.error('Error deleting calendar:', error);
      }
    }
  };

  const handleDeletePoll = async (id) => {
    if (window.confirm('Möchten Sie diese Umfrage wirklich löschen?')) {
      try {
        await pollService.deletePoll(id);
        await deletePoll(id);
        await refreshPolls();
      } catch (error) {
        console.error('Error deleting poll:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-gray-900 dark:text-white"
          >
            Willkommen zurück, {user?.name}!
          </motion.h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Hier ist eine Übersicht über Ihre Termine und Umfragen
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-primary-100 dark:bg-primary-900 rounded-lg">
                  <SafeIcon icon={FiCalendar} className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Kalender
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {calendars.length}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-success-100 dark:bg-success-900 rounded-lg">
                  <SafeIcon icon={FiUsers} className="w-6 h-6 text-success-600 dark:text-success-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Buchungen
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.totalBookings}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Umfragen
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {polls.length}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Schnellaktionen
            </h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/calendar/new">
                <Button className="w-full sm:w-auto">
                  <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                  Neuer Kalender
                </Button>
              </Link>
              <Link to="/poll/new">
                <Button variant="outline" className="w-full sm:w-auto">
                  <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                  Neue Umfrage
                </Button>
              </Link>
            </div>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calendars */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Meine Kalender
                </h2>
                <Link to="/calendar/new">
                  <Button size="sm" variant="ghost">
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  </Button>
                </Link>
              </div>

              {calendars.length === 0 ? (
                <div className="text-center py-8">
                  <SafeIcon icon={FiCalendar} className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Noch keine Kalender erstellt
                  </p>
                  <Link to="/calendar/new">
                    <Button size="sm">Ersten Kalender erstellen</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {calendars.map((calendar) => (
                    <div
                      key={calendar.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {calendar.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {calendar.slots?.length || 0} Zeitslots
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => copyShareLink(calendar.shareId)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Link kopieren"
                        >
                          <SafeIcon icon={FiCopy} className="w-4 h-4" />
                        </button>
                        <Link
                          to={`/calendar/edit/${calendar.id}`}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Bearbeiten"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => window.open(`/#/book/${calendar.shareId}`, '_blank')}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Buchungsseite öffnen"
                        >
                          <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCalendar(calendar.id)}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Löschen"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Polls */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Meine Umfragen
                </h2>
                <Link to="/poll/new">
                  <Button size="sm" variant="ghost">
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  </Button>
                </Link>
              </div>

              {polls.length === 0 ? (
                <div className="text-center py-8">
                  <SafeIcon icon={FiBarChart3} className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Noch keine Umfragen erstellt
                  </p>
                  <Link to="/poll/new">
                    <Button size="sm">Erste Umfrage erstellen</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {polls.map((poll) => {
                    const totalVotes = poll.options.reduce((sum, option) => sum + option.votes.length, 0);
                    return (
                      <div
                        key={poll.id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {poll.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {totalVotes} Stimmen
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => copyShareLink(poll.shareId, 'poll')}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Link kopieren"
                          >
                            <SafeIcon icon={FiCopy} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => window.open(`/#/poll/${poll.shareId}`, '_blank')}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Umfrage öffnen"
                          >
                            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                          </button>
                          <Link
                            to={`/poll/${poll.shareId}/results`}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Ergebnisse anzeigen"
                          >
                            <SafeIcon icon={FiBarChart3} className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDeletePoll(poll.id)}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title="Löschen"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;